import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ReportBody } from "@/components/ReportBody";
import CaptureForm from "@/components/CaptureForm";
import { WatermarkBanner } from "@/components/Watermark";
import { Building2, Loader2, Download } from "lucide-react";

// Stable per-browser session id so repeat photo opens don't double-count.
function getSessionId() {
  const KEY = "af_pub_session";
  let v = localStorage.getItem(KEY);
  if (!v) {
    v = crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, v);
  }
  return v;
}

export default function PublicReport() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const buyerId = searchParams.get("b") || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    axios.get(`${API}/public/report/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const onPhotoView = useCallback((idx) => {
    const total = (data?.lead?.photos || []).length || 1;
    axios.post(`${API}/public/report/${token}/photo-view`, {
      photo_index: idx,
      total_photos: total,
      session_id: sessionId.current,
      source: "public",
      team_member_id: buyerId,
    }).catch(() => { /* swallow — analytics best-effort */ });
  }, [data, token, buyerId]);

  const accent = data?.agent?.brand_accent || "#10B981";
  const company = data?.agent?.brand_company || data?.agent?.name || "the team";

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="bg-[var(--ink)] text-white print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: accent }}><Building2 size={14} /></span>
            <span className="font-display font-extrabold">iSpeedToClose</span>
          </Link>
          <div className="flex items-center gap-3">
            {data && (
              <a
                href={`${API}/public/report/${token}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-white/20 hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                data-testid="public-report-download-pdf"
              >
                <Download size={13} /> Download PDF
              </a>
            )}
            <span className="text-xs text-white/55 uppercase tracking-widest hidden sm:inline">Shared Property Analysis</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {loading ? (
          <div className="card text-center py-16 text-[var(--ink-2)]">
            <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading…
          </div>
        ) : !data ? (
          <div className="card text-center py-16">
            <div className="font-display font-bold text-xl mb-2">Report not available</div>
            <p className="text-[var(--ink-2)] mb-5">This link may have been disabled by the sender or doesn&apos;t exist.</p>
            <Link to="/" className="btn-outline inline-flex">Visit iSpeedToClose</Link>
          </div>
        ) : (
          <>
            <ReportBody
              lead={data.lead}
              report={data.report}
              onPhotoView={onPhotoView}
              photoEndpoint={`/public/report/${token}/photos`}
            />
            <CaptureForm reportToken={token} accent={accent} company={company} personaName={data?.agent?.ai_persona_name || ""} />
          </>
        )}
      </main>

      {data?.agent?.watermark && <WatermarkBanner />}
    </div>
  );
}
